// ─────────────────────────────────────────────────────────────────────────────
// GestioneOrigineNc — pagina admin del master per gestire la tassonomia
// dell'ORIGINE delle Non Conformità (come/dove la NC è stata rilevata).
//
// Lookup globale (visibile a tutte le aziende), sullo stesso modello di
// `categoria_nc`. Distinta dalla CATEGORIA:
//   - Categoria  = cosa è la NC (corpi estranei, igiene, …)
//   - Origine    = come/dove è stata rilevata (autocontrollo, VII, audit,
//                  reclamo, analisi, ispezione ASL/NAS, …)
//
// UI clone strutturalmente identica a GestioneCategorieNc (vive in App.js).
// Colore arancio per distinguerla visivamente dalla pagina categorie (viola)
// e icona gitFork (più sorgenti → un registro).
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react';
import { LT } from './ui/tokens';
import I, { Ico } from './ui/icons';
import { Btn, Card, Chip } from './ui/components';

function GestioneOrigineNc({ operatore, onBack, apiBase, apiFetch, toast, chiediConferma }) {
  const [lista,       setLista]       = useState([]);
  const [form,        setForm]        = useState(null);
  const [salvando,    setSalvando]    = useState(false);
  const [errore,      setErrore]      = useState('');
  const [cerca,       setCerca]       = useState('');
  const [filtroStato, setFiltroStato] = useState('attive'); // 'attive' | 'archiviate' | 'tutte'

  useEffect(() => { carica(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const carica = async () => {
    const res  = await apiFetch(`${apiBase}/admin/origini-nc`);
    const data = await res.json().catch(() => []);
    setLista(Array.isArray(data) ? data : []);
  };

  const handleSalva = async () => {
    if (!form.descrizione || !form.descrizione.trim()) {
      setErrore('Descrizione obbligatoria');
      return;
    }
    setErrore('');
    setSalvando(true);
    try {
      const url    = form.id ? `${apiBase}/admin/origini-nc/${form.id}` : `${apiBase}/admin/origini-nc`;
      const method = form.id ? 'PUT' : 'POST';
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          descrizione:       form.descrizione.trim(),
          descrizioneEstesa: form.descrizione_estesa || null,
          ordine:            form.ordine ? parseInt(form.ordine) : 0,
          attivo:            form.attivo !== false,
        })
      });
      if (res.status === 409) {
        setErrore('Esiste già un\'origine con questa descrizione.');
        setSalvando(false);
        return;
      }
      if (!res.ok) throw new Error();
      setForm(null);
      await carica();
      toast('✓ Origine salvata!');
    } catch {
      setErrore('Errore nel salvataggio. Riprova.');
    }
    setSalvando(false);
  };

  const labelStyle = {
    display: 'block', fontSize: 11, fontWeight: 700, color: LT.n600,
    textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6,
  };
  const inputStyle = {
    width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans,
    color: LT.n900, background: '#fff', border: `1px solid ${LT.n200}`,
    borderRadius: LT.r.md, outline: 'none', boxSizing: 'border-box',
  };
  const textareaStyle = { ...inputStyle, resize: 'vertical', minHeight: 80, fontFamily: LT.sans };

  // ── FORM ────────────────────────────────────────────────────────────────
  if (form !== null) {
    return (
      <div style={{ padding: 24, maxWidth: 720, margin: '0 auto' }}>
        <div style={{ marginBottom: 18 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: LT.n500,
            textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4,
          }}>
            Origini NC · {form.id ? 'Modifica' : 'Nuova'}
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, flexWrap: 'wrap',
          }}>
            <div>
              <div style={{
                fontSize: 22, fontWeight: 700, color: LT.n900,
                letterSpacing: '-.01em', lineHeight: 1.2,
              }}>
                {form.id ? (form.descrizione || 'Origine') : 'Nuova origine NC'}
              </div>
              <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
                {form.id ? 'Modifica l\'origine e la sua definizione operativa' : 'Registra una nuova origine per le non conformità'}
              </div>
            </div>
            <Btn kind="outline" icon="x" onClick={() => { setForm(null); setErrore(''); }}>
              Annulla
            </Btn>
          </div>
        </div>

        <Card pad={20} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{
              width: 34, height: 34, borderRadius: LT.r.md,
              background: LT.arancioSoft, color: LT.arancio,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Ico d={I.gitFork} size={16} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: LT.n900 }}>Dati origine</div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Descrizione *</label>
            <input style={inputStyle} value={form.descrizione || ''}
              onChange={e => setForm({ ...form, descrizione: e.target.value })}
              placeholder="es. Reclamo cliente" />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Definizione operativa</label>
            <textarea style={textareaStyle} value={form.descrizione_estesa || ''}
              onChange={e => setForm({ ...form, descrizione_estesa: e.target.value })}
              placeholder="Descrizione estesa che aiuta l'operatore a scegliere correttamente questa origine. Viene mostrata sotto al select durante la compilazione di una NC." />
            <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 4 }}>
              Spiega chiaramente in quali casi usare questa origine e in quali no.
            </div>
          </div>

          <div style={{ marginBottom: 0 }}>
            <label style={labelStyle}>Ordine di visualizzazione</label>
            <input type="number" style={inputStyle} value={form.ordine ?? 0}
              onChange={e => setForm({ ...form, ordine: e.target.value })}
              placeholder="0" />
            <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 4 }}>
              Numero usato per l'ordinamento nelle liste (più basso = più in alto).
            </div>
          </div>
        </Card>

        {/* Stato (solo modifica) */}
        {form.id && (
          <Card pad={20} style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{
                width: 34, height: 34, borderRadius: LT.r.md,
                background: LT.verdeSoft, color: LT.verde,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Ico d={I.shield} size={16} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: LT.n900 }}>Stato</div>
            </div>

            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              padding: '10px 12px', borderRadius: LT.r.md,
              border: `1px solid ${LT.n150}`,
              background: form.attivo !== false ? LT.verdeSoft : LT.rossoSoft,
            }}>
              <Chip
                kind={form.attivo !== false ? 'verde' : 'rosso'}
                icon={form.attivo !== false ? 'check' : 'x'}
                size="md"
              >
                {form.attivo !== false ? 'Attiva' : 'Archiviata'}
              </Chip>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 12.5, color: LT.n600, lineHeight: 1.35 }}>
                  {form.attivo !== false
                    ? 'Selezionabile dagli utenti nella compilazione di nuove NC.'
                    : 'Non più selezionabile per nuove NC. Lo storico resta filtrabile.'}
                </div>
              </div>
              <Btn
                kind={form.attivo === false ? 'success' : 'outline'}
                size="sm"
                icon={form.attivo === false ? 'check' : 'x'}
                onClick={async () => {
                  if (form.attivo !== false) {
                    const ok = await chiediConferma(
                      'Archiviare questa origine?',
                      `"${form.descrizione}" non sarà più selezionabile per nuove NC. Le NC che la usavano restano invariate e filtrabili.`
                    );
                    if (!ok) return;
                    setForm({ ...form, attivo: false });
                  } else {
                    setForm({ ...form, attivo: true });
                  }
                }}
              >
                {form.attivo === false ? 'Riattiva' : 'Archivia'}
              </Btn>
            </div>
          </Card>
        )}

        {errore && (
          <div style={{
            background: LT.rossoSoft, color: LT.rosso, borderRadius: LT.r.md,
            padding: '10px 14px', marginBottom: 12, fontSize: 13, fontWeight: 500,
            display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #fecaca',
          }}>
            <Ico d={I.alert} size={14} /> {errore}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <Btn kind="outline" onClick={() => { setForm(null); setErrore(''); }}>Annulla</Btn>
          <div style={{ flex: 1 }}>
            <Btn kind="primary" block icon="check" onClick={handleSalva} disabled={salvando}>
              {salvando ? 'Salvataggio…' : 'Salva origine'}
            </Btn>
          </div>
        </div>
      </div>
    );
  }

  // ── LISTA ────────────────────────────────────────────────────────────────
  const q = cerca.toLowerCase();
  const filtrati = lista.filter(o => {
    if (filtroStato === 'attive'     && o.attivo === false) return false;
    if (filtroStato === 'archiviate' && o.attivo !== false) return false;
    return `${o.descrizione || ''} ${o.descrizione_estesa || ''}`.toLowerCase().includes(q);
  });
  const countAttive      = lista.filter(o => o.attivo !== false).length;
  const countArchiviate  = lista.filter(o => o.attivo === false).length;

  return (
    <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>
      <div style={{ marginBottom: 18 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: LT.n500,
          textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4,
        }}>
          {new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, flexWrap: 'wrap',
        }}>
          <div>
            <div style={{
              fontSize: 22, fontWeight: 700, color: LT.n900,
              letterSpacing: '-.01em', lineHeight: 1.2,
            }}>
              Origini NC
            </div>
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
              Tassonomia globale dell'origine delle non conformità (visibile a tutte le aziende)
            </div>
          </div>
          <Btn kind="primary" icon="plus" onClick={() => setForm({ attivo: true, ordine: 0 })}>
            Nuova origine
          </Btn>
        </div>
      </div>

      <Card pad={14} style={{ marginBottom: 14 }}>
        <div style={{
          display: 'flex', gap: 0, marginBottom: 10,
          padding: 3, borderRadius: LT.r.md,
          background: LT.n100, border: `1px solid ${LT.n150}`,
        }}>
          {[
            { key: 'attive',     label: 'Attive',     count: countAttive     },
            { key: 'archiviate', label: 'Archiviate', count: countArchiviate },
            { key: 'tutte',      label: 'Tutte',      count: lista.length    },
          ].map(opt => {
            const on = filtroStato === opt.key;
            return (
              <button key={opt.key} onClick={() => setFiltroStato(opt.key)} style={{
                flex: 1, padding: '7px 10px', borderRadius: LT.r.sm,
                border: 'none',
                background: on ? '#fff' : 'transparent',
                color: on ? LT.n900 : LT.n500,
                fontSize: 12.5, fontWeight: on ? 700 : 500,
                cursor: 'pointer', fontFamily: LT.sans,
                boxShadow: on ? LT.sh1 : 'none',
                transition: 'all .15s',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
                {opt.label}
                <span style={{
                  fontSize: 11, fontWeight: 700,
                  padding: '1px 6px', borderRadius: LT.r.full,
                  background: on ? LT.n100 : 'transparent',
                  color: on ? LT.n600 : LT.n400,
                }}>{opt.count}</span>
              </button>
            );
          })}
        </div>
        <div style={{ position: 'relative' }}>
          <div style={{
            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            color: LT.n400, display: 'flex',
          }}>
            <Ico d={I.search} size={15} />
          </div>
          <input
            style={{ ...inputStyle, paddingLeft: 36 }}
            placeholder="Cerca per descrizione o definizione..."
            value={cerca}
            onChange={e => setCerca(e.target.value)}
          />
        </div>
      </Card>

      <div style={{
        fontSize: 11, fontWeight: 700, color: LT.n500,
        textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8,
      }}>
        {filtrati.length} {filtrati.length === 1 ? 'origine' : 'origini'}
        {cerca && ` per "${cerca}"`}
      </div>

      {filtrati.length === 0 ? (
        <Card pad={24} style={{ textAlign: 'center' }}>
          <div style={{
            width: 44, height: 44, borderRadius: LT.r.lg,
            background: LT.n100, color: LT.n400,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 10px',
          }}>
            <Ico d={I.gitFork} size={20} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: LT.n700 }}>
            {cerca ? `Nessun risultato per "${cerca}"` : 'Nessuna origine in questa vista'}
          </div>
        </Card>
      ) : (
        filtrati.map(o => (
          <Card
            key={o.id}
            pad={14}
            hover
            onClick={() => setForm({ ...o })}
            style={{
              marginBottom: 10,
              opacity: o.attivo === false ? 0.65 : 1,
              borderLeft: `4px solid ${o.attivo === false ? LT.n300 : LT.arancio}`,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 40, height: 40, borderRadius: LT.r.md,
                background: LT.arancioSoft, color: LT.arancio,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Ico d={I.gitFork} size={18} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 2,
                }}>
                  <div style={{ fontSize: 14.5, fontWeight: 700, color: LT.n900 }}>
                    {o.descrizione}
                  </div>
                  {o.attivo === false && (
                    <Chip kind="rosso" icon="x" size="sm">Archiviata</Chip>
                  )}
                </div>
                {o.descrizione_estesa && (
                  <div style={{ fontSize: 12.5, color: LT.n600, lineHeight: 1.4,
                    overflow: 'hidden', display: '-webkit-box',
                    WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  }}>
                    {o.descrizione_estesa}
                  </div>
                )}
              </div>
              <Ico d={I.chevronR} size={16} color={LT.n300} />
            </div>
          </Card>
        ))
      )}
    </div>
  );
}

export default GestioneOrigineNc;
